import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { ConfigProvider, theme as antdTheme, Form, Input, InputNumber, Space, Typography, Button, TimePicker, Drawer } from 'antd';
import { AntRangePicker, AntDropdown, dayjs } from '../UI';

const { Text } = Typography;

const GoalFormDrawer = ({
  isOpen,
  onClose,
  mode, // 'add' or 'edit'
  goalForm,
  setGoalForm,
  onSubmit,
  isSubmitting,
  isDark: propIsDark
}) => {
  const { mode: themeMode } = useTheme();
  const isDark = propIsDark !== undefined ? propIsDark : themeMode === 'dark';
  const [form] = Form.useForm();

  // Sync form values when goalForm changes (especially for edit mode)
  useEffect(() => {
    if (isOpen && goalForm) {
      form.setFieldsValue({
        title: goalForm.title || '',
        description: goalForm.description || '',
        type: goalForm.type || undefined,
        targetValue: goalForm.targetValue ? parseFloat(goalForm.targetValue) : undefined,
        unit: goalForm.unit || undefined,
        dateRange: goalForm.startDate && goalForm.endDate 
          ? [dayjs(goalForm.startDate), dayjs(goalForm.endDate)] 
          : null,
        endTime: goalForm.endTime || null,
      });
    }
  }, [isOpen, goalForm, form]);

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  const title = mode === 'add' ? 'Add New Goal' : 'Edit Goal';
  const subtitle = mode === 'add' 
    ? 'Set a goal to track performance and progress'
    : 'Update goal details and tracking information';
  const submitButtonText = mode === 'add' 
    ? (isSubmitting ? 'Adding...' : 'Add Goal')
    : (isSubmitting ? 'Updating...' : 'Update Goal');

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorBgElevated: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
          colorBgContainer: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
          borderRadius: 12,
        },
      }}
    >
      <Drawer
        title={
          <div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '4px' }}>
              {title}
            </div>
            <div style={{ fontSize: '14px', fontWeight: 'normal', opacity: 0.7 }}>
              {subtitle}
            </div>
          </div>
        }
        placement="right"
        onClose={onClose}
        open={isOpen}
        width={672}
        zIndex={9999}
        styles={{
          body: {
            padding: '24px',
          },
          header: {
            borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
            padding: '24px',
          },
        }}
        style={{
          background: isDark ? "rgba(0, 0, 0, 0.95)" : "#ffffff",
        }}
      >
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Goal Title */}
              <Form.Item
                name="title"
                label={
                  <Text strong style={{ fontSize: '14px' }}>
                    Goal Title <Text type="danger">*</Text>
                  </Text>
                }
                rules={[{ required: true, message: 'Please enter a goal title!' }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  value={goalForm.title}
                  onChange={(e) =>
                    setGoalForm({ ...goalForm, title: e.target.value })
                  }
                  placeholder="Enter goal title"
                  size="large"
                />
              </Form.Item>

              {/* Description */}
              <Form.Item
                name="description"
                label={
                  <Text strong style={{ fontSize: '14px' }}>
                    Description <Text type="danger">*</Text>
                  </Text>
                }
                rules={[{ required: true, message: 'Please enter a description!' }]}
                style={{ marginBottom: 0 }}
              >
                <Input.TextArea
                  value={goalForm.description}
                  onChange={(e) =>
                    setGoalForm({
                      ...goalForm,
                      description: e.target.value,
                    })
                  }
                  placeholder="Enter goal description"
                  rows={3}
                  size="large"
                />
              </Form.Item>

              {/* Goal Type */}
              <Form.Item
                name="type"
                label={
                  <Text strong style={{ fontSize: '14px' }}>
                    Goal Type <Text type="danger">*</Text>
                  </Text>
                }
                rules={[{ required: true, message: 'Please select a goal type!' }]}
                style={{ marginBottom: 0 }}
              >
                <AntDropdown
                  value={goalForm.type}
                  onChange={(value) =>
                    setGoalForm({ ...goalForm, type: value })
                  }
                  placeholder="Select goal type"
                  options={[
                    { label: "Productivity", value: "productivity" },
                    { label: "Time", value: "time" },
                    { label: "Application", value: "application" },
                    { label: "Custom", value: "custom" },
                    { label: "Mixed", value: "mixed" },
                  ]}
                  size="large"
                />
              </Form.Item>

              {/* Target Value and Unit */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item
                  name="targetValue"
                  label={
                    <Text strong style={{ fontSize: '14px' }}>
                      Target Value <Text type="danger">*</Text>
                    </Text>
                  }
                  rules={[{ required: true, message: 'Please enter a target value!' }]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    value={goalForm.targetValue ? parseFloat(goalForm.targetValue) : undefined}
                    onChange={(value) =>
                      setGoalForm({
                        ...goalForm,
                        targetValue: value?.toString() || "",
                      })
                    }
                    placeholder="100"
                    size="large"
                    style={{ width: '100%' }}
                    min={0}
                  />
                </Form.Item>
                <Form.Item
                  name="unit"
                  label={
                    <Text strong style={{ fontSize: '14px' }}>
                      Unit <Text type="danger">*</Text>
                    </Text>
                  }
                  rules={[{ required: true, message: 'Please select a unit!' }]}
                  style={{ marginBottom: 0 }}
                >
                  <AntDropdown
                    value={goalForm.unit}
                    onChange={(value) =>
                      setGoalForm({ ...goalForm, unit: value })
                    }
                    placeholder="Select unit"
                    options={[
                      { label: "% (percentage)", value: "%" },
                      { label: "hours (time)", value: "hours" },
                      { label: "tasks (work items)", value: "tasks" },
                      {
                        label: "applications (software usage)",
                        value: "applications",
                      },
                      { label: "days (time period)", value: "days" },
                      {
                        label: "projects (work deliverables)",
                        value: "projects",
                      },
                    ]}
                    size="large"
                  />
                </Form.Item>
              </div>

              {/* Date Range */}
              <Form.Item
                name="dateRange"
                label={
                  <Text strong style={{ fontSize: '14px' }}>
                    Date Range <Text type="danger">*</Text>
                  </Text>
                }
                rules={[{ required: true, message: 'Please select a date range!' }]}
                style={{ marginBottom: 0 }}
              >
                <AntRangePicker
                  value={
                    goalForm.startDate && goalForm.endDate
                      ? [
                          dayjs(goalForm.startDate),
                          dayjs(goalForm.endDate),
                        ]
                      : null
                  }
                  onChange={(dates, dateStrings) => {
                    if (dates && dateStrings && dateStrings.length === 2) {
                      setGoalForm({
                        ...goalForm,
                        startDate: dateStrings[0],
                        endDate: dateStrings[1],
                      });
                    } else {
                      setGoalForm({
                        ...goalForm,
                        startDate: "",
                        endDate: "",
                      });
                    }
                  }}
                  disabledDate={(current) => {
                    // Disable all past dates (before today)
                    return current && current.isBefore(dayjs(), 'day');
                  }}
                  placeholder={["Start date", "End date"]}
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  size="large"
                />
              </Form.Item>

              {/* End Time Picker */}
              <Form.Item
                name="endTime"
                label={
                  <Text strong style={{ fontSize: '14px' }}>
                    End Time (Optional)
                  </Text>
                }
                style={{ marginBottom: 0 }}
                help={
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Target time by which goal should be completed (e.g., "12:00 PM"). Must be at least 30 minutes apart from other goals on the same date.
                  </Text>
                }
              >
                <TimePicker
                  value={goalForm.endTime}
                  onChange={(time) => {
                    setGoalForm({
                      ...goalForm,
                      endTime: time,
                    });
                  }}
                  format="h:mm A"
                  placeholder="Select end time (e.g., 12:00 PM)"
                  style={{ width: '100%' }}
                  size="large"
                  use12Hours
                />
              </Form.Item>

              {/* Footer Buttons */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  marginTop: '24px',
                  paddingTop: '20px',
                  borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
                }}
              >
                <Button
                  onClick={onClose}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  size="large"
                >
                  {submitButtonText}
                </Button>
              </div>
            </Space>
          </Form>
      </Drawer>
    </ConfigProvider>
  );
};

export default GoalFormDrawer;

